import { AvatarV2, AvatarGroupV2, DataGrid, TooltipV2 } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { toastNotification } from '../../components/toast/Toast';
import { useCreateDocument } from '../../queries/use-create-document';
import { calcRelativeTime, formatDateTime, formatMixedCase } from '../../utilities/formatting';
import { GenericWorksheet } from '../../types/worksheet';
import { WorksheetStatusIcon } from '../worksheet-v2/WorksheetStatusIcon';
import Dropzone from '../../components/dropzone/Dropzone';
import WorksheetOutstandingItems from '../../components/worksheet-outstanding-items/WorksheetOutstandingItems';
import FileAttachments from '../trial-balance/FileAttachments';
import queryClient from '../../query-client';

type OtherScheduleRowProps = {
    id: string;
    otherSchedule: GenericWorksheet;
    disabled: boolean;
    onUploading: (uploading: boolean) => void;
    /** Id of the worksheet with their file drawer open */
    currentFileDrawer?: string;
    /** Callback to open the file drawer for a worksheet */
    onFileDrawerOpen: (id?: string) => void;
};

function OtherScheduleRow({
    id,
    otherSchedule,
    disabled,
    onUploading,
    currentFileDrawer,
    onFileDrawerOpen,
}: OtherScheduleRowProps): JSX.Element {
    const createDocumentQuery = useCreateDocument();
    const fileLimit = 30;

    async function handleFileUpload(files: File[]): Promise<void> {
        onUploading(true);
        const uploads = [];
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: otherSchedule.id },
                {
                    onSuccess: () => {
                        toastNotification('success', 'New file uploaded!');
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        await Promise.allSettled(uploads);
        queryClient.invalidateQueries(['other-schedules', otherSchedule.workpaperId]);
        onUploading(false);
    }

    return (
        <Dropzone onDrop={handleFileUpload} disabled={disabled} fileLimit={fileLimit}>
            <DataGrid.Row id={id}>
                <div className="flex gap-2 py-2 items-center">
                    <WorksheetStatusIcon status={otherSchedule.status} />
                    <Link to={otherSchedule.id}>
                        <p className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer">{otherSchedule.name}</p>
                    </Link>
                </div>
                <TooltipV2 message={formatDateTime(otherSchedule.updatedAt)}>
                    <p className="text-gray-700 text-sm">{calcRelativeTime(otherSchedule.updatedAt)}</p>
                </TooltipV2>
                <p className="text-sm text-gray-600">{'entries' in otherSchedule ? otherSchedule.entries.length : 0}</p>
                <FileAttachments worksheet={otherSchedule} isOpen={currentFileDrawer === otherSchedule.id} onOpen={onFileDrawerOpen} />
                {/* OUTSTANDING */}
                <WorksheetOutstandingItems worksheet={otherSchedule} smallScreen={false} />
                {/* ASSIGNED */}
                {otherSchedule.assigned.length > 0 && (
                    <AvatarGroupV2 size="xs">
                        {otherSchedule.assigned.map((assignee) => {
                            return (
                                <AvatarV2
                                    key={assignee.id}
                                    alt={assignee.firstName[0] + assignee.lastName[0]}
                                    color={assignee.profileHexColour}
                                    tooltip={`${assignee.firstName} ${assignee.lastName} ${
                                        assignee.role ? `(${formatMixedCase(assignee.role)})` : ''
                                    }`}
                                    src={assignee.avatarUrl}
                                />
                            );
                        })}
                    </AvatarGroupV2>
                )}
            </DataGrid.Row>
        </Dropzone>
    );
}

export default OtherScheduleRow;
